import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { ListingApplicationPayload } from '#/api/ats';
import { apiCreateListingApplication } from '#/api/listingApplication/apiCreateListingApplication';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { useIsomorphicEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackAppliedToListing } from '#/modules/listing/analytics/trackAppliedToListing';
import { useJobAppliedConfetti } from '#/modules/listing/jobs/components/JobAppliedConfetti/useJobAppliedConfetti';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import {
  userDashboardApplicationHistoryRoute,
  userDashboardViewEmploymentOnboardingRoute,
} from '#/modules/userDashboard/routing/userDashboard.routes';
import { isDefined } from '#/utils/functional/type';
import { hasResumeDb } from '#/utils/resumeDatabase';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId | ApiEventId;
  listingType: ApiListingApplicationListingType;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  onApplied: (listingApplicationId: ApiListingApplicationId) => void;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
};

export function useListingApplicationSubmissionAsUser({
  listingId,
  listingType,
  orgId,
  orgType,
  onApplied,
  listingApplicationFailure,
}: Args) {
  const navigate = useNavigate();
  const { i18n, locale } = useI18n();
  const { isomorphicEnvironment } = useIsomorphicEnvironment();
  const Confetti = useJobAppliedConfetti();

  const getInitialUserValues = useCallback(
    ({ user }: { user: ApiUser }) =>
      listingApplicationFailure?.initialValues ?? {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        resumeS3FileId:
          listingType !== 'VOLOP' ? (user.resumeId ?? '') : undefined,
        consentAts: isomorphicEnvironment.isEU ? false : undefined,
      },
    [listingApplicationFailure, listingType, isomorphicEnvironment],
  );

  const submitListingApplicationAsUser = useCallback(
    async ({
      user,
      values,
    }: {
      user: ApiUser;
      values: ListingApplicationPayload;
    }) => {
      const showOnboarding =
        !isDefined(user.isRecruitable) &&
        hasResumeDb(user, isomorphicEnvironment, locale);

      const newListingApplication = await apiCreateListingApplication(values);
      onApplied(newListingApplication.id);

      trackAppliedToListing({
        listingId,
        listingType,
        orgId: orgId ?? null,
        orgType: orgType ?? null,
        isNewResume: user.resumeId !== values.resumeS3FileId,
        hasAts: true,
      });

      const targetPath =
        showOnboarding && listingType !== 'VOLOP'
          ? userDashboardViewEmploymentOnboardingRoute.with({
              slug: 'get-discovered',
              applied: true,
            })
          : userDashboardApplicationHistoryRoute.with({});

      const toastDataOverride = {
        body: i18n('Thank you! Your interest has been submitted successfully.'),
      };

      if (showOnboarding) {
        await navigate(targetPath);
        setTimeout(
          () =>
            Confetti.showSimple(
              targetPath,
              listingType === 'VOLOP' ? toastDataOverride : {},
            ),
          1000,
        );
      } else {
        setTimeout(
          () =>
            Confetti.showWithRedirect(
              targetPath,
              listingType === 'VOLOP' ? toastDataOverride : {},
            ),
          1000,
        );
      }
    },
    [
      Confetti,
      listingId,
      listingType,
      locale,
      navigate,
      i18n,
      onApplied,
      orgId,
      orgType,
      isomorphicEnvironment,
    ],
  );

  return {
    getInitialUserValues,
    submitListingApplicationAsUser,
  };
}
